/**
 * Attribution passthrough — bodyhackguide.co -> bhglabs.co
 *
 * Paid traffic lands here with utm_* and rdt_cid params, then hops to the store on a
 * different domain. Ad-platform click cookies are first-party per-domain, so
 * nothing survives that hop on its own: the store sees a bare referral and the
 * sale can't be traced back to the ad that paid for it.
 *
 * So we do it ourselves:
 *   1. capture()  — read the ad params off the landing URL, persist 30 days
 *                   (localStorage + a first-party cookie, either can survive
 *                   the other being cleared).
 *   2. decorate() — stamp those params onto every outbound bhglabs.co link.
 *
 * Two things worth knowing before editing this file:
 *
 * - The click interceptor, not decorate(), is the actual guarantee. React
 *   re-renders links constantly; a link decorated on mount can be replaced by
 *   an undecorated one a tick later. The capture-phase click handler rewrites
 *   href at the moment of navigation, so it cannot be raced. decorate() exists
 *   so the status bar / "copy link address" show the real URL too.
 *
 * - We deliberately do NOT set rel="noreferrer" on store links, and strip it if
 *   present. noreferrer drops the Referer header, which is one of the signals
 *   the store side uses to attribute the visit.
 */

const STORAGE_KEY = "bhg_attribution";
const COOKIE_NAME = "bhg_attr";
/** 90 days, matching the first-party cookie the store sets on its side. */
const TTL_DAYS = 90;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Ad/click params worth carrying across the domain hop.
 * The utm_* set is enumerated rather than globbed so we forward exactly what
 * the store's snippet reads, and don't smuggle unrelated `utm_`-prefixed junk
 * from other tools into the order record.
 */
const TRACKED_PARAM =
  /^(utm_source|utm_medium|utm_campaign|utm_content|utm_term|utm_id|rdt_cid|fbclid|gclid|ttclid|msclkid|epik|irclickid)$/i;

/** Matches bhglabs.co and any subdomain, but not e.g. notbhglabs.co. */
const STORE_HOST = /(^|\.)bhglabs\.co$/i;

export const STORE_ORIGIN = "https://bhglabs.co";
export const STORE_SHOP_URL = `${STORE_ORIGIN}/shop/`;
/** The single active offer. One code, everywhere — see storeUrl(). */
export const STORE_COUPON = "BHG20";
/** Affiliate ref the store credits BodyHackGuide sales to. Every store link carries it. */
export const STORE_REF = "zmgqyxku";

type AttributionBag = Record<string, string>;
interface StoredAttribution {
  params: AttributionBag;
  ts: number;
}

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

/* ------------------------------------------------------------------ */
/* storage                                                             */
/* ------------------------------------------------------------------ */

function readCookie(): StoredAttribution | null {
  try {
    const match = document.cookie.match(
      "(?:^|; )" + COOKIE_NAME.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)",
    );
    if (!match) return null;
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    return parsed && typeof parsed === "object" && parsed.params ? (parsed as StoredAttribution) : null;
  } catch {
    return null;
  }
}

function writeCookie(value: StoredAttribution) {
  try {
    // Lax (not Strict) so the cookie is readable on a normal inbound ad click.
    // Secure only on https, otherwise localhost dev silently drops it.
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}` +
      `; max-age=${Math.floor(TTL_MS / 1000)}; path=/; SameSite=Lax${secure}`;
  } catch {
    /* cookies disabled — localStorage may still work */
  }
}

function readStorage(): StoredAttribution | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && parsed.params ? (parsed as StoredAttribution) : null;
  } catch {
    return null;
  }
}

function writeStorage(value: StoredAttribution) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* Safari private mode / quota — cookie is the fallback */
  }
}

function isFresh(entry: StoredAttribution | null): entry is StoredAttribution {
  return !!entry && typeof entry.ts === "number" && Date.now() - entry.ts < TTL_MS;
}

/* ------------------------------------------------------------------ */
/* public API                                                          */
/* ------------------------------------------------------------------ */

/** Stored ad params, or {} if none/expired. Prefers whichever store is fresher. */
export function getAttribution(): AttributionBag {
  if (!isBrowser()) return {};
  const fromStorage = readStorage();
  const fromCookie = readCookie();
  const candidates = [fromStorage, fromCookie].filter(isFresh);
  if (candidates.length === 0) return {};
  candidates.sort((a, b) => b.ts - a.ts);
  return candidates[0].params;
}

/**
 * Read ad params off the current URL and persist them.
 *
 * Last-touch: a fresh ad click overwrites the stored bag, because the most
 * recent paid click is the one that should get credit. A visit with no ad
 * params leaves the existing bag alone (organic revisits shouldn't wipe it).
 */
export function captureAttribution(): AttributionBag {
  if (!isBrowser()) return {};
  let incoming: AttributionBag = {};
  try {
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (TRACKED_PARAM.test(key) && value) incoming[key] = value;
    });
  } catch {
    incoming = {};
  }

  if (Object.keys(incoming).length === 0) return getAttribution();

  const entry: StoredAttribution = { params: incoming, ts: Date.now() };
  writeStorage(entry);
  writeCookie(entry);
  return incoming;
}

/** Default labels for a store click from this site's own pages. */
const SITE_REFERRAL_UTMS = {
  utm_source: "bodyhackguide",
  utm_medium: "referral",
  utm_campaign: "bhg20_site",
};

/** The Reddit bridge campaign's labels, the same ones the static landers hardcode. */
const REDDIT_AD_UTMS = {
  utm_source: "reddit",
  utm_medium: "paid",
  utm_campaign: "bhg20_bridge",
};

/** A Reddit post or comment link: Reddit as the source, social as the medium, the site's own campaign. */
const REDDIT_ORGANIC_UTMS = {
  utm_source: "reddit",
  utm_medium: "social",
  utm_campaign: "bhg20_site",
};

/** The utm_medium values that mean a paid click. Matched without case. */
const PAID_MEDIUM = /^(paid|cpc|ppc|paid[_-]social)$/i;

/**
 * Whether the visitor's own landing URL tied them to Reddit, and how:
 *   - "ad" for the click id Reddit adds to every ad click (rdt_cid), or a utm_source naming Reddit with a paid
 *     utm_medium (paid, cpc, ppc, paid_social);
 *   - "organic" for a utm_source naming Reddit with any other medium or none (a tagged post or comment link,
 *     utm_medium=social);
 *   - null for everything else, so only Reddit visits get Reddit labels.
 * Until 2026-10-24 any utm_source naming Reddit counted as the paid campaign. Keys are matched without case, the
 * same way TRACKED_PARAM captured them.
 */
function redditVisit(stored: AttributionBag): "ad" | "organic" | null {
  let source = "";
  let medium = "";
  for (const [key, value] of Object.entries(stored)) {
    const name = key.toLowerCase();
    if (name === "rdt_cid" && value) return "ad";
    if (name === "utm_source") source = value;
    if (name === "utm_medium") medium = value;
  }
  if (!/reddit/i.test(source)) return null;
  return PAID_MEDIUM.test(medium.trim()) ? "ad" : "organic";
}

/**
 * Build a store URL carrying attribution + the coupon.
 * `utmContent` labels the placement (lander / homepage / scorecard / ...).
 */
export function storeUrl(utmContent: string, base: string = STORE_SHOP_URL): string {
  const stored = getAttribution();
  try {
    const url = new URL(base);
    // Default labels, overridden below by anything really captured from the URL.
    // Reddit labels only go on visits that came in from Reddit, and the paid set
    // only on Reddit ad clicks, so organic Reddit, direct and other referral
    // clicks don't get counted as the paid campaign.
    const reddit = redditVisit(stored);
    const defaults = reddit === "ad" ? REDDIT_AD_UTMS : reddit === "organic" ? REDDIT_ORGANIC_UTMS : SITE_REFERRAL_UTMS;
    for (const [key, value] of Object.entries(defaults)) url.searchParams.set(key, value);
    url.searchParams.set("utm_content", utmContent);
    for (const [key, value] of Object.entries(stored)) {
      // Keep the placement label we were called with — it's more specific than
      // whatever utm_content the inbound ad URL happened to carry.
      if (key.toLowerCase() === "utm_content") continue;
      url.searchParams.set(key, value);
    }
    url.searchParams.set("ref", STORE_REF);
    url.searchParams.set("coupon", STORE_COUPON);
    return url.toString();
  } catch {
    return base;
  }
}

function isStoreLink(anchor: HTMLAnchorElement): boolean {
  const raw = anchor.getAttribute("href");
  if (!raw) return false;
  try {
    return STORE_HOST.test(new URL(raw, window.location.href).hostname);
  } catch {
    return false;
  }
}

/** Stamp stored params + coupon onto one anchor. Idempotent. */
function decorateAnchor(anchor: HTMLAnchorElement, stored: AttributionBag) {
  const raw = anchor.getAttribute("href");
  if (!raw) return;
  try {
    const url = new URL(raw, window.location.href);
    if (!STORE_HOST.test(url.hostname)) return;
    for (const [key, value] of Object.entries(stored)) url.searchParams.set(key, value);
    if (!url.searchParams.has("ref")) url.searchParams.set("ref", STORE_REF);
    if (!url.searchParams.has("coupon")) url.searchParams.set("coupon", STORE_COUPON);
    anchor.setAttribute("href", url.toString());

    // noreferrer strips the Referer header the store uses to attribute the
    // visit. Keep noopener (that's a security control, and unrelated).
    const rel = anchor.getAttribute("rel");
    if (rel && /noreferrer/i.test(rel)) {
      const cleaned = rel.split(/\s+/).filter((t) => t.toLowerCase() !== "noreferrer").join(" ").trim();
      if (cleaned) anchor.setAttribute("rel", cleaned);
      else anchor.removeAttribute("rel");
    }
  } catch {
    /* unparseable href — leave it alone */
  }
}

/** Decorate every store link currently in the DOM. */
export function decorateStoreLinks(root: ParentNode = document): number {
  if (!isBrowser()) return 0;
  const stored = getAttribution();
  const anchors = Array.from(root.querySelectorAll<HTMLAnchorElement>('a[href*="bhglabs.co"]'));
  let count = 0;
  for (const anchor of anchors) {
    if (!isStoreLink(anchor)) continue;
    decorateAnchor(anchor, stored);
    count++;
  }
  return count;
}

let initialised = false;

/**
 * Wire up attribution once, at app boot.
 * Safe to call more than once; only the first call does anything.
 */
export function initAttribution(): void {
  if (!isBrowser() || initialised) return;
  initialised = true;

  captureAttribution();
  decorateStoreLinks();

  // The guarantee. Capture phase so we rewrite before React Router or any
  // other handler sees the click, and before the browser starts navigating.
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as Element | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor || !isStoreLink(anchor)) return;
      decorateAnchor(anchor, getAttribution());
    },
    true,
  );

  // Re-decorate as React swaps content in, so hover/status-bar and
  // "copy link address" show the real destination too.
  try {
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        decorateStoreLinks();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  } catch {
    /* no MutationObserver — the click interceptor still covers us */
  }
}
